/**
 * Pure display/state logic for the M5-G Your Offers surfaces
 * (task:m5-your-offers-page, task:m5-celebration, task:m5-empty-states,
 * task:m5-ladder-ui). Kept side-effect-free so the section-split,
 * once-guard, empty-state discrimination, and ladder locked/unlocked
 * rendering rules are unit-testable without a DOM or network.
 */

import type { OfferCardEstimatedSavings } from "@sill/shared/components/marketplace/offer-card";

import type {
  OfferEstimatedSavings,
  OfferForBuyer,
  StandingMatch,
} from "./marketplace-offers-api";

/**
 * Maps a server-computed dollar-back estimate (snake_case wire shape) onto the
 * shared offer card's `estimatedSavings`. The ONE converter every consumer
 * card surface uses. Missing or null stays null — the card then renders no
 * dollar line, never "$0".
 */
export function toCardSavings(
  savings: OfferEstimatedSavings | null | undefined,
): OfferCardEstimatedSavings | null {
  if (!savings) return null;
  return {
    basis: savings.basis,
    basisCents: savings.basis_cents,
    cents: savings.cents,
  };
}

/** The existing verification flow entry point (App.tsx: "/dashboard?view=passport",
 * the same destination PublishPage's "Get verified to save more" CTA and the
 * global tier explainer already route into). The ladder's locked-tier CTA
 * reuses this route rather than inventing a second verification entry
 * point. */
export const VERIFY_FINANCES_HREF = "/dashboard?view=passport";

/** The publish action entry point (M2: PassportPublishBlock at ?view=passport;
 * /dashboard/publish redirects here). */
export const PUBLISH_HREF = "/dashboard?view=passport";

/** The Buyer Passport view. Aliases PUBLISH_HREF (same route today) but named
 * for the "View Passport" affordance on the Your Offers surface so the intent
 * (review your live passport) is distinct from the publish action — and so the
 * two can't silently drift apart. */
export const PASSPORT_HREF = PUBLISH_HREF;

/** Competition is meaningful as soon as the buyer has a choice between two
 * distinct agents. Both the inbox banner and share artifact use this boundary. */
export const COMPETITION_MILESTONE_AGENT_COUNT = 2;

export function distinctCompetingAgentCount(
  madeForYou: readonly Pick<OfferForBuyer, "pro_user_id">[],
  availableToYou: readonly Pick<StandingMatch, "pro_user_id">[],
): number {
  return new Set(
    [
      ...madeForYou.map((offer) => offer.pro_user_id),
      ...availableToYou.map((match) => match.pro_user_id),
    ].filter(Boolean),
  ).size;
}

export type OffersEmptyState = "populated" | "quiet" | "unpublished";

/**
 * Distinguishes published-but-quiet from unpublished
 * (task:m5-empty-states, scenario "inbox empty states distinguish
 * published-quiet from unpublished"). Never guesses "unpublished" from an
 * empty inbox alone — that requires the buyer's own publish state.
 */
export function resolveOffersEmptyState(input: {
  published: boolean;
  madeForYouCount: number;
  availableToYouCount: number;
}): OffersEmptyState {
  if (input.madeForYouCount > 0 || input.availableToYouCount > 0) {
    return "populated";
  }
  return input.published ? "quiet" : "unpublished";
}

const CELEBRATION_STORAGE_PREFIX = "sill.offers-celebration-shown.";

function celebrationStorageKey(userKey: string): string {
  return `${CELEBRATION_STORAGE_PREFIX}${userKey}`;
}

/**
 * Celebration once-guard (task:m5-celebration). The backend `celebration`
 * flag is a LIVE recompute of the distinct-agent competition milestone. It
 * stays true on every subsequent visit. The FE owns "show ONCE" by remembering,
 * per signed-in user, that the moment already played.
 */
export function hasCelebrationBeenShown(
  userKey: string,
  storage: Pick<Storage, "getItem"> = safeStorage(),
): boolean {
  try {
    return storage.getItem(celebrationStorageKey(userKey)) === "1";
  } catch {
    return false;
  }
}

export function markCelebrationShown(
  userKey: string,
  storage: Pick<Storage, "setItem"> = safeStorage(),
): void {
  try {
    storage.setItem(celebrationStorageKey(userKey), "1");
  } catch {
    // storage unavailable (privacy mode) — celebration may re-show; not
    // worth failing the render over.
  }
}

/**
 * Whether to render the celebration banner THIS render — true only when
 * the backend says the threshold is live AND the FE hasn't recorded
 * showing it before for this user.
 */
export function shouldShowCelebration(
  celebration: boolean,
  alreadyShown: boolean,
): boolean {
  return celebration && !alreadyShown;
}

function safeStorage(): Storage {
  if (typeof window === "undefined") {
    return noopStorage();
  }
  try {
    return window.localStorage;
  } catch {
    return noopStorage();
  }
}

function noopStorage(): Storage {
  const store = new Map<string, string>();
  return {
    get length() {
      return store.size;
    },
    clear: () => store.clear(),
    getItem: (key: string) => store.get(key) ?? null,
    key: (index: number) => Array.from(store.keys())[index] ?? null,
    removeItem: (key: string) => {
      store.delete(key);
    },
    setItem: (key: string, value: string) => {
      store.set(key, value);
    },
  };
}

/**
 * Parses a price-bracket display string (e.g. "$1.0-1.1M", "$1.0M-$1.1M",
 * "$500K-$600K") into a rough midpoint dollar figure, for the ONE derived-$
 * line the design spec allows (invariant 5: "≈ $9,000 (0.9% of sale
 * price)" — "allowed ONLY with explicit estimate basis labeling ... without
 * the basis label, cut it"). Returns null for any shape it can't parse
 * confidently — callers must treat null as "no basis, don't derive a
 * dollar figure."
 */
export function parsePriceBracketMidpoint(
  bracket: string | undefined,
): number | null {
  if (!bracket) return null;
  const matches = [...bracket.matchAll(/([\d.]+)\s*([MK])?/gi)];
  const values: number[] = [];
  // A bracket like "$1.0-1.1M" shares one trailing unit across both
  // numbers; scan right-to-left so an unmarked earlier number inherits the
  // next-seen unit.
  let trailingUnit: string | null = null;
  for (let i = matches.length - 1; i >= 0; i--) {
    const [, rawNum, rawUnit] = matches[i];
    const num = Number.parseFloat(rawNum);
    if (!Number.isFinite(num)) continue;
    const unit = rawUnit ? rawUnit.toUpperCase() : trailingUnit;
    if (rawUnit) trailingUnit = rawUnit.toUpperCase();
    if (!unit) continue;
    const multiplier = unit === "M" ? 1_000_000 : 1_000;
    values.unshift(num * multiplier);
  }
  if (values.length === 0) return null;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return sum / values.length;
}

/**
 * The estimate-basis label required alongside any derived-$ line
 * (invariant 5). Callers render this directly beneath the derived amount —
 * never the amount alone.
 */
export function estimateBasisLabel(bracket: string | undefined): string | null {
  if (!bracket) return null;
  return `est. on your ${bracket} bracket`;
}

/**
 * Computes the derived-$ estimate for a percent-valued rebate item against
 * a frozen price bracket, paired with its mandatory basis label. Returns
 * null when there's no bracket to derive from — callers must render the
 * bare percent line instead of fabricating a figure.
 */
export function estimateRebateDollars(
  rebatePct: number | undefined,
  bracket: string | undefined,
): { amount: number; basisLabel: string } | null {
  if (rebatePct == null || !Number.isFinite(rebatePct)) return null;
  const midpoint = parsePriceBracketMidpoint(bracket);
  const basisLabel = estimateBasisLabel(bracket);
  if (midpoint == null || basisLabel == null) return null;
  return { amount: (rebatePct / 100) * midpoint, basisLabel };
}

/** True iff an offer's items include a value-bearing rebate lane. */
export function rebatePctFromOffer(
  offer: Pick<OfferForBuyer, "items">,
): number | undefined {
  const rebate = offer.items.rebate?.find((item) => item.value != null);
  return rebate?.value ?? undefined;
}
